import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { UserTable } from "./UserTable";
import { UserListHeader } from "./UserListHeader";
import { EditUserDialog } from "./EditUserDialog";
import { RoleDialog } from "./RoleDialog";
import type { Profile } from "./types";

const UserList = () => {
  const { toast } = useToast();
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);

  const { data: users, refetch } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*');
      
      if (error) {
        toast({
          title: "Error fetching users",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }
      
      return profiles as Profile[];
    }
  });

  const handleUserAction = async (userId: string, action: 'activate' | 'suspend') => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: action === 'activate' ? 'active' as const : 'suspended' as const })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `User ${action === 'activate' ? 'activated' : 'suspended'} successfully`,
      });

      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update user status",
        variant: "destructive",
      });
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          phone_number: editingUser.phone_number,
        })
        .eq('id', editingUser.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "User information updated successfully",
      });

      setIsEditDialogOpen(false);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update user information",
        variant: "destructive",
      });
    }
  };

  const handleRoleChange = async (role: string) => {
    if (!selectedUser) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role })
        .eq('id', selectedUser.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `User role updated to ${role} successfully`,
      });

      setIsRoleDialogOpen(false);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update user role",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <UserListHeader />
      <UserTable
        users={users}
        onStatusChange={handleUserAction}
        onEdit={(user) => {
          setEditingUser(user);
          setIsEditDialogOpen(true);
        }}
        onRoleChange={(user) => {
          setSelectedUser(user);
          setIsRoleDialogOpen(true);
        }}
      />

      <EditUserDialog
        user={editingUser}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onUserChange={setEditingUser}
        onSave={handleEditUser}
      />

      {selectedUser && (
        <RoleDialog
          userId={selectedUser.id}
          currentRole={selectedUser.role || 'user'}
          open={isRoleDialogOpen}
          onOpenChange={setIsRoleDialogOpen}
          onSave={handleRoleChange}
        />
      )}
    </div>
  );
};

export default UserList;